import React, { createContext, useContext, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserProfile } from '../services/userService';
import { resetLiveWriteApproval } from '../utils/impersonationWriteGate';

interface ImpersonatedOrganization {
  id: string;
  name: string;
  type: 'store' | 'vendor' | 'admin';
  email?: string;
  phone?: string;
  address?: string;
  status: 'active' | 'inactive' | 'pending';
  userCount?: number;
  productCount?: number;
  lastActive?: string;
  plan?: string;
  features?: string[];
}

interface ImpersonatedBrand {
  id: string;
  name: string;
  logoUrl?: string | null;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedRole: 'admin' | 'vendor' | 'store' | null;
  impersonatedUserType: 'store' | 'vendor_supplier' | null;
  impersonatedOrg: ImpersonatedOrganization | null;
  impersonatedBrand: ImpersonatedBrand | null;
  startImpersonation: (role: 'admin' | 'vendor' | 'store', userType?: 'store' | 'vendor_supplier') => void;
  startImpersonationWithOrg: (organization: ImpersonatedOrganization) => void;
  startImpersonationWithBrand: (brand: ImpersonatedBrand) => void;
  stopImpersonation: () => void;
  getEffectiveRole: (userProfile: UserProfile | null) => 'admin' | 'vendor' | 'store';
  getEffectiveUserType: (userProfile: UserProfile | null) => 'store' | 'vendor_supplier';
}

interface SavedImpersonationState {
  isImpersonating: boolean;
  role: 'admin' | 'vendor' | 'store' | null;
  userType: 'store' | 'vendor_supplier' | null;
  org: ImpersonatedOrganization | null;
  brand: ImpersonatedBrand | null;
}

const EMPTY_IMPERSONATION_STATE: SavedImpersonationState = {
  isImpersonating: false,
  role: null,
  userType: null,
  org: null,
  brand: null,
};

const readSavedImpersonation = (): SavedImpersonationState => {
  try {
    const saved = localStorage.getItem('admin-impersonation');
    if (!saved) return EMPTY_IMPERSONATION_STATE;
    const { isImpersonating, role, userType, org, brand } = JSON.parse(saved);
    if (isImpersonating && role) {
      return { isImpersonating: true, role, userType: userType || null, org: org || null, brand: brand || null };
    }
    return EMPTY_IMPERSONATION_STATE;
  } catch (error) {
    console.error('Error loading impersonation state:', error);
    try {
      localStorage.removeItem('admin-impersonation');
    } catch {
      // best effort
    }
    return EMPTY_IMPERSONATION_STATE;
  }
};

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  // Read once, synchronously, as each field's initial state — not in a mount
  // effect. A mount effect leaves a one-frame window where every consumer
  // (DashboardGate included) sees the pre-impersonation defaults, which is
  // exactly the frame a gate deciding "which expensive query set to fire" runs
  // in.
  const [initialImpersonation] = useState(readSavedImpersonation);
  const [isImpersonating, setIsImpersonating] = useState(initialImpersonation.isImpersonating);
  const [impersonatedRole, setImpersonatedRole] = useState<'admin' | 'vendor' | 'store' | null>(
    initialImpersonation.role
  );
  const [impersonatedUserType, setImpersonatedUserType] = useState<'store' | 'vendor_supplier' | null>(
    initialImpersonation.userType
  );
  const [impersonatedOrg, setImpersonatedOrg] = useState<ImpersonatedOrganization | null>(initialImpersonation.org);
  const [impersonatedBrand, setImpersonatedBrand] = useState<ImpersonatedBrand | null>(initialImpersonation.brand);

  /**
   * Drop everything cached for the account we are leaving.
   *
   * Several queries key on the route alone (`dashboard-cameras`,
   * `dashboard-integrations`), so without this the next context can be served
   * the previous one's results with no refetch. Cancelling matters as much as
   * clearing: a request already in flight was sent with the old account's
   * headers and would otherwise land afterwards and refill what was just wiped.
   *
   * This lives here rather than at each call site because every path that
   * starts or ends impersonation needs it, and keeping it at the call sites is
   * exactly how the Store Management and Brand dashboard buttons were missed.
   */
  const resetAccountScopedCache = useCallback(() => {
    queryClient.cancelQueries();
    queryClient.clear();
    // A new or ended impersonation never inherits the last one's live-change approval.
    resetLiveWriteApproval();
  }, [queryClient]);

  const startImpersonation = (role: 'admin' | 'vendor' | 'store', userType?: 'store' | 'vendor_supplier') => {
    setIsImpersonating(true);
    setImpersonatedRole(role);
    setImpersonatedUserType(userType || null);
    setImpersonatedOrg(null);
    setImpersonatedBrand(null);
    
    // Save to localStorage
    localStorage.setItem('admin-impersonation', JSON.stringify({
      isImpersonating: true,
      role,
      userType: userType || null,
      org: null,
      brand: null
    }));
    resetAccountScopedCache();
  };

  const startImpersonationWithOrg = (organization: ImpersonatedOrganization) => {
    setIsImpersonating(true);
    setImpersonatedRole(organization.type);
    setImpersonatedUserType(organization.type === 'vendor' ? 'vendor_supplier' : 'store');
    setImpersonatedOrg(organization);
    setImpersonatedBrand(null);
    
    // Save to localStorage
    localStorage.setItem('admin-impersonation', JSON.stringify({
      isImpersonating: true,
      role: organization.type,
      userType: organization.type === 'vendor' ? 'vendor_supplier' : 'store',
      org: organization,
      brand: null
    }));
    resetAccountScopedCache();
  };

  const startImpersonationWithBrand = (brand: ImpersonatedBrand) => {
    setIsImpersonating(true);
    setImpersonatedRole('vendor');
    setImpersonatedUserType('vendor_supplier');
    setImpersonatedOrg(null);
    setImpersonatedBrand(brand);

    // Save to localStorage
    localStorage.setItem('admin-impersonation', JSON.stringify({
      isImpersonating: true,
      role: 'vendor',
      userType: 'vendor_supplier',
      org: null,
      brand
    }));
    resetAccountScopedCache();
  };

  const stopImpersonation = () => {
    setIsImpersonating(false);
    setImpersonatedRole(null);
    setImpersonatedUserType(null);
    setImpersonatedOrg(null);
    setImpersonatedBrand(null);
    
    // Remove from localStorage
    localStorage.removeItem('admin-impersonation');
    resetAccountScopedCache();
  };

  const getEffectiveRole = (userProfile: UserProfile | null): 'admin' | 'vendor' | 'store' => {
    if (isImpersonating && impersonatedRole) {
      return impersonatedRole;
    }
    
    if (!userProfile) return 'store';
    
    if (userProfile.role === 'admin' || userProfile.role === 'super_admin') return 'admin';
    if (userProfile.userType === 'vendor_supplier') return 'vendor';
    return 'store';
  };

  const getEffectiveUserType = (userProfile: UserProfile | null): 'store' | 'vendor_supplier' => {
    if (isImpersonating && impersonatedUserType) {
      return impersonatedUserType;
    }

    if (!userProfile || !userProfile.userType) return 'store';
    // 'group' (a buying group / head office parent org) has no store or
    // vendor shell to impersonate into and never reaches `/app/dashboard`
    // under normal routing (`onboardingService.getNextStep` sends it to
    // `/onboarding/group-pending` or `/app/group` instead) — this fallback
    // only covers a stray direct navigation there.
    if (userProfile.userType === 'group') return 'store';
    return userProfile.userType;
  };

  const value = {
    isImpersonating,
    impersonatedRole,
    impersonatedUserType,
    impersonatedOrg,
    impersonatedBrand,
    startImpersonation,
    startImpersonationWithOrg,
    startImpersonationWithBrand,
    stopImpersonation,
    getEffectiveRole,
    getEffectiveUserType
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};
